/**
 * Logger utility for frontend application
 * Only logs in development mode
 * Usage::
 * this.logger.info('Info message');
 * this.logger.success('Success message');
 * this.logger.warn('Warning message');
 * this.logger.error('Error message');
 * this.logger.debug('Debug message');
 * If trying to access object, use  JSON.stringify(response.data, null, 2) to format the object
 */

class Logger {
  constructor(namespace) {
    this.namespace = namespace;
    this.isDevelopment = process.env.NODE_ENV === "development";
  }

  /**
   * Format the log message with timestamp and namespace
   */
  formatMessage(message, type) {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${type}] [${this.namespace}]: ${message}`;
  }

  /**
   * Format arguments for logging (handles objects, arrays, etc.)
   */
  formatArgs(...args) {
    return args
      .map((arg) => {
        if (typeof arg === "object" && arg !== null) {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(" ");
  }

  /**
   * Log info messages
   */
  info(...args) {
    if (this.isDevelopment) {
      const formattedArgs = this.formatArgs(...args);
      const message = this.formatMessage(formattedArgs, "INFO");
      console.log("%c" + message, "color: #2196F3");
    }
  }

  /**
   * Log success messages
   */
  success(...args) {
    if (this.isDevelopment) {
      const formattedArgs = this.formatArgs(...args);
      const message = this.formatMessage(formattedArgs, "SUCCESS");
      console.log("%c" + message, "color: #4CAF50");
    }
  }

  /**
   * Log warning messages
   */
  warn(...args) {
    if (this.isDevelopment) {
      const formattedArgs = this.formatArgs(...args);
      const message = this.formatMessage(formattedArgs, "WARN");
      console.warn("%c" + message, "color: #FFC107");
    }
  }

  /**
   * Log error messages
   */
  error(...args) {
    if (this.isDevelopment) {
      const formattedArgs = this.formatArgs(...args);
      const message = this.formatMessage(formattedArgs, "ERROR");
      console.error("%c" + message, "color: #F44336");
    }
  }

  /**
   * Log debug messages
   */
  debug(...args) {
    if (this.isDevelopment) {
      const formattedArgs = this.formatArgs(...args);
      const message = this.formatMessage(formattedArgs, "DEBUG");
      console.debug("%c" + message, "color: #9C27B0");
    }
  }
}

/**
 * Create a new logger instance with a specific namespace
 */
export const createLogger = (namespace) => {
  return new Logger(namespace);
};

/**
 * Default logger instance
 */
export default new Logger("App");
